import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { useLocation } from 'react-router-dom';

import useComponentMounted from '../../../hooks/useComponentMounted';
import useNavigation from '../../../hooks/useNavigation';
import UserContext from '../../../context/UserContext';
import Lead, {
  InitialLeadState,
  AllLeadStates,
  AllSalesStages,
} from '../../Model/Lead';
import { groupLeadsBySalesStage } from '../../utils/lead';
import LeadsContext from '../../context/LeadsContext';
import ExternalCoachContext from '../../context/ExternalCoachContext';
import CoachesListContext from '../../context/CoachesListContext';
import { LeadContractModal } from '../ContractModal';
import LoadingOverlay from '../LoadingOverlay';
import ColoredHeader from '../ColoredHeader';
import PageContent from '../PageContent';
import FilterButtons from '../FilterButtons';
import DownloadCSVButton from '../DownloadCSVButton';
import DateFilter from '../DateFilter';
import LeadsModal from './components/v2/LeadModal';
import StateFilter from './components/StateFilter';
import NotesModal from './components/NotesModal';
import LeadsTable, {
  LeadsTableType,
} from './components/LeadsTable';
import DisqualifyModal from './components/DisqualifyModal';

import {
  SalesStagesOptions,
  salesStagesFilterChecks,
  FinalStatusSalesStages,
} from './utils';
import {
  Container,
  Title,
  Description,
  HeaderRow,
} from './styles';
import texts from './texts';
import InsideSalesSelectors from './components/InsideSalesSelectors';

const EMPTY_ROWS = [];
const BASE_FILE_NAME = 'leads';
const CSV_HEADERS = [
  'id',
  'submitDate',
  'name',
  'email',
  'gender',
  'phoneNumber',
  'country',
  'state',
  'source',
];

const Leads = ({
  title,
  description,
  enableCoachSelect,
  isISView,
}) => {
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showUnqualifiedModal, setShowUnqualifiedModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [requireOutcome, setRequireOutcome] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Filters states
  const [activeSalesStageFilter, setActiveSalesStageFilter] = useState(
    isISView ? SalesStagesOptions.NEW_LEADS : SalesStagesOptions.ALL,
  );
  const [selectedLeadStates, setSelectedLeadStates] = useState(isISView ? AllLeadStates : InitialLeadState);
  // we only show sales stage filters for IS final filter view
  const [selectedSalesStages, setSelectedSalesStages] = useState(isISView ? FinalStatusSalesStages : AllSalesStages);

  const {
    leads,
    loadingLeads,
    filteredLeads,
    selectedPeriod,
    setSelectedPeriod,
    setIsAgentId,
    setIsISDashboardView,
  } = useContext(LeadsContext);

  const {
    userDoc,
  } = useContext(UserContext);

  const {
    externalCoachDoc,
  } = useContext(ExternalCoachContext);

  const { coachesList } = useContext(CoachesListContext);

  const isComponentMountedRef = useComponentMounted();

  const selectNewCoach = useCallback((coach) => {
    if (coach) {
      setIsLoading(true);
    }
  }, []);

  const onOnboardingLinkClick = () => {
    setShowContractModal(true);
  };

  const onNotesClick = () => {
    setShowNotesModal(true);
  };

  /*
    The leads table should only be rendered if we have data available. That could happen in these two situations:
      1. The coach select is enabled and a coach has been selected.
      2. The coach select is not enabled, but the external coach document is ready to be used.
  */
  const shouldRenderTable = enableCoachSelect || !!externalCoachDoc;

  useEffect(() => {
    // reset the IS dashboard view when the leads page is loaded
    setIsISDashboardView(false);
    if (!loadingLeads) {
      setIsLoading(false);
    }
  }, [loadingLeads, setIsISDashboardView]);

  // Extract Lead ID from URL
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlLeadId = urlParams.get('leadId');

  // Find lead document using URL ID and set it as selected
  useEffect(() => {
    const getLead = async () => {
      const leadDoc = await Lead.getLeadById(urlLeadId);
      // Since the lead id is coming from an url param, we should check if it exists
      if (isComponentMountedRef.current && leadDoc) {
        setSelectedLead(leadDoc);
        setShowLeadModal(true);
      }
    };
    if (urlLeadId && !isLoading) {
      getLead();
    }
  }, [
    urlLeadId,
    isLoading,
    isComponentMountedRef,
  ]);

  const { clearSearchParams } = useNavigation();

  const handleLeadModalClose = () => {
    clearSearchParams();
    setRequireOutcome(false);
    setShowLeadModal(false);
  };

  const [rowsBySalesStage, salesStagesCounters] = useMemo(
    () => groupLeadsBySalesStage(leads, SalesStagesOptions, salesStagesFilterChecks),
    [leads],
  );

  const shouldRenderRows = !isLoading && !!rowsBySalesStage[activeSalesStageFilter];

  return (
    <Container>
      <ColoredHeader>
        <HeaderRow>
          <div>
            <Title>{title}</Title>
            {!!description && <Description>{description}</Description>}
          </div>
          <DownloadCSVButton
            csvHeaders={CSV_HEADERS}
            rows={filteredLeads}
            fileName={BASE_FILE_NAME}
            disabled={!filteredLeads.length}
          />
        </HeaderRow>
        <HeaderRow>
          {!!enableCoachSelect && (
            <InsideSalesSelectors
              onCoachChange={selectNewCoach}
              onIsUserChange={setIsAgentId}
            />
          )}
          {/* we only show stage filters for coach view & IS final filter view */}
          {(!isISView || activeSalesStageFilter === SalesStagesOptions.FINAL_STATUS) && (
            <StateFilter
              initialStates={isISView ? FinalStatusSalesStages : InitialLeadState}
              onSelect={isISView ? setSelectedSalesStages : setSelectedLeadStates}
              isISView={isISView}
            />
          )}
          <DateFilter
            selectedPeriod={selectedPeriod}
            onChange={setSelectedPeriod}
          />
        </HeaderRow>
        {(shouldRenderTable && isISView) && (
          <HeaderRow>
            <FilterButtons
              filterOptions={SalesStagesOptions}
              optionCounters={salesStagesCounters}
              onSelectFilter={setActiveSalesStageFilter}
              optionTexts={texts.filters}
              activeOption={activeSalesStageFilter}
              fullWidth
              // we don't want to show the count badge for "All" and "Final Status" options in the filter buttons
              optionsWithoutBadges={[SalesStagesOptions.ALL, SalesStagesOptions.FINAL_STATUS]}
            />
          </HeaderRow>
        )}
      </ColoredHeader>
      <PageContent>
        {shouldRenderTable && (
          <>
            <LeadsTable
              rows={shouldRenderRows ? rowsBySalesStage[activeSalesStageFilter] : EMPTY_ROWS}
              selectLead={setSelectedLead}
              setShowUnqualifiedModal={setShowUnqualifiedModal}
              setShowQuestionsModal={setShowLeadModal}
              onOnboardingLinkClick={onOnboardingLinkClick}
              selectedLeadStates={selectedLeadStates}
              // we show sales stages filter only for IS view and when the "Final Status" tab is selected
              selectedSalesStages={(isISView && activeSalesStageFilter === SalesStagesOptions.FINAL_STATUS)
                ? selectedSalesStages
                : AllSalesStages}
              type={isISView ? LeadsTableType.IS_LEADS : LeadsTableType.LEADS}
              userId={userDoc.id}
              userName={userDoc.name}
              onNotesClick={onNotesClick}
              coachesList={coachesList}
              selectedPeriod={selectedPeriod}
              activeSalesStageFilter={activeSalesStageFilter}
            />
          </>
        )}
        {selectedLead && (
          <>
            {!!showLeadModal && (
              <LeadsModal
                lead={selectedLead}
                showModal={showLeadModal}
                onClose={handleLeadModalClose}
                userId={userDoc.id}
                userName={userDoc.name}
                isInsideSalesView={isISView}
                requireOutcome={requireOutcome}
              />
            )}
            <NotesModal
              lead={selectedLead}
              showModal={showNotesModal}
              onClose={() => setShowNotesModal(false)}
            />
            {!!showContractModal && (
              <LeadContractModal
                lead={selectedLead}
                coachId={selectedLead.coach}
                showModal={showContractModal}
                showPostCommitmentConfiguration={isISView}
                onClose={() => {
                  setShowContractModal(false);
                  setSelectedLead(null);
                }}
                isISView={isISView}
              />
            )}
            <DisqualifyModal
              lead={selectedLead}
              showModal={showUnqualifiedModal}
              userId={userDoc.id}
              userName={userDoc.name}
              onClose={() => setShowUnqualifiedModal(false)}
            />
          </>
        )}
      </PageContent>
      <LoadingOverlay isLoading={loadingLeads || isLoading} />
      {/* This div is used to test the loading time of the leads page */}
      {!loadingLeads && !isLoading && <div data-testid="leads-container" />}
    </Container>
  );
};

Leads.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  enableCoachSelect: PropTypes.bool,
  isISView: PropTypes.bool,
};

Leads.defaultProps = {
  description: '',
  enableCoachSelect: false,
  isISView: false,
};

export default compose(
  observer,
)(Leads);
